//  Note that we are intentionally using this Project type (and not the model-typings one), since it includes the associations that we need.
import type { Project } from '../../../../types';
import type { NotificationsProjectSettings } from '../../../../../shared/settings/project/notifications';
import type { Notification } from '../../../../../shared/model-typings';

type ProjectNotificationsAssociation = Project['notifications'] | ['']; // Note that this empty string is intentional, it's used to say "no notifications for this project".

export type ProjectNotificationsSettings = NotificationsProjectSettings;

const defaultSettings: ProjectNotificationsSettings = {
  notificationDefaultEnabled: false,
  notificationDefaultEmailScanUsers: [],
  notificationDefaultSlackScan: false,
  notificationDefaultEmailScanUserType: 'current',
};

const minimalSlackEnabledNotification = {
  channel: 'SCAN',
  service: 'SLACK',
  subscribed_users: null,
};

//  This takes the incoming array of associated Notifications and converts them into a settings
//  object which better maps to the UI components.
function convertNotificationsArrayToSettings(
  notifications: ProjectNotificationsAssociation,
): ProjectNotificationsSettings {
  if (!notifications || (notifications.length === 1 && notifications[0] === '')) {
    return defaultSettings;
  }

  //  This is needed to please Typescript
  const projectNotifications = notifications as Notification[];
  const [emailNotification] = projectNotifications.filter((notification) => notification.service === 'EMAIL');
  const notificationDefaultEmailScanUsers = emailNotification?.subscribed_users || [];

  return {
    notificationDefaultEnabled: !!emailNotification,
    notificationDefaultEmailScanUsers,
    notificationDefaultSlackScan: projectNotifications.some((notification) => notification.service === 'SLACK'),
    //  Note that this depends on the users we fetch in the hook itself, so this may be updated later
    //  depending on how many users are actually in the org.
    notificationDefaultEmailScanUserType: defaultSettings.notificationDefaultEmailScanUserType,
  };
}

export const pickNotificationsSettingsFromProject = (project: Project): ProjectNotificationsSettings =>
  convertNotificationsArrayToSettings(project.notifications);

export const convertNotificationsSettingsToProject = (
  notifications: ProjectNotificationsSettings,
): Partial<Project> => {
  //  The "array of empty string" means "no notifications for this project" to the backend.
  if (!notifications.notificationDefaultEnabled) {
    return {
      //  Note that this is how the backend knows to delete all notifications for this project.
      //  It's a bit of a hack, but changing the backend is out of scope for now.
      notifications: [''] as unknown as Project['notifications'],
    };
  }
  //  The only fields from Notification that the backend actually requires are:
  return {
    notifications: [
      {
        channel: 'SCAN',
        service: 'EMAIL',
        subscribed_users: notifications.notificationDefaultEmailScanUsers,
      },
      ...(notifications.notificationDefaultSlackScan ? [minimalSlackEnabledNotification] : []),
    ] as Project['notifications'],
  };
};
